import React, { useState, useEffect } from 'react';
import { getNewNotifications, getOldNotifications, markAllAsRead } from '../services/notificationService';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './NotificationList.module.css';

const NotificationList = ({ onBack }) => {
    // Check notification permissions
    const { hasShowNotification } = useCachedPermissions();

    // Call all hooks first to comply with React hooks rules
    const [activeTab, setActiveTab] = useState('new');
    const [newNotifications, setNewNotifications] = useState([]);
    const [oldNotifications, setOldNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [markingAsRead, setMarkingAsRead] = useState(false);

    // Fetch notifications when component mounts or tab changes
    useEffect(() => {
        // Only fetch if user has permission
        if (hasShowNotification) {
            fetchNotifications();
        }
    }, [activeTab, currentPage, hasShowNotification]);

    const fetchNotifications = async () => {
        setLoading(true);
        setError(null);

        try {
            let response;
            if (activeTab === 'new') {
                response = await getNewNotifications(currentPage, 16);
                setNewNotifications(response.data.data);
            } else {
                response = await getOldNotifications(currentPage, 16);
                setOldNotifications(response.data.data);
            }
            setTotalPages(response.data.last_page);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1); // Reset to first page when changing tabs
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleMarkAllAsRead = async () => {
        setMarkingAsRead(true);
        try {
            await markAllAsRead();
            // Refresh notifications after marking as read
            await fetchNotifications();
        } catch (error) {
            console.error('Error marking notifications as read:', error);
            setError(error.message);
        } finally {
            setMarkingAsRead(false);
        }
    };

    const getCurrentNotifications = () => {
        return activeTab === 'new' ? newNotifications : oldNotifications;
    };

    const formatTime = (timeString) => {
        // You can implement proper time formatting here
        // For now, return as is since the API might provide formatted strings
        return timeString || 'Recently';
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'ticket':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                    </svg>
                );
            case 'project':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9,22 9,12 15,12 15,22" />
                    </svg>
                );
            case 'payment':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                );
            default:
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                );
        }
    };

    const currentNotifications = getCurrentNotifications();

    // If user doesn't have notification permission, show access denied
    if (!hasShowNotification) {
        return (
            <div className={styles.notificationListContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button onClick={onBack} className={styles.backButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <h2 className={styles.headerTitle}>Notifications</h2>
                    </div>
                </div>
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18.364 5.636l-4.95 4.95M9.464 14.536l4.95-4.95M21 12a9 9 0 11-18 0 9 9 0 0118 0zM12 8v4M12 16h.01" />
                        </svg>
                    </div>
                    <h3 className={styles.emptyTitle}>Access Denied</h3>
                    <p className={styles.emptyMessage}>You do not have permission to view notifications.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.notificationListContainer}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button onClick={onBack} className={styles.backButton}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    <h2 className={styles.headerTitle}>Notifications</h2>
                </div>
                <div className={styles.headerRight}>
                    {activeTab === 'new' && (
                        <button
                            onClick={handleMarkAllAsRead}
                            className={styles.markAllButton}
                            disabled={markingAsRead || newNotifications.length === 0}
                        >
                            {markingAsRead ? (
                                <>
                                    <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                                    </svg>
                                    Marking...
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20,6 9,17 4,12" />
                                    </svg>
                                    Mark All as Read
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.tabsContainer}>
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'new' ? styles.activeTab : ''}`}
                        onClick={() => handleTabChange('new')}
                    >
                        New ({newNotifications.length})
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'old' ? styles.activeTab : ''}`}
                        onClick={() => handleTabChange('old')}
                    >
                        Old ({oldNotifications.length})
                    </button>
                </div>
            </div>

            {/* Notifications List */}
            <div className={styles.notificationsContainer}>
                {loading ? (
                    <div className={styles.loadingState}>
                        <div className={styles.loadingSpinner}></div>
                        <p>Loading notifications...</p>
                    </div>
                ) : error ? (
                    <div className={styles.errorState}>
                        <div className={styles.errorIcon}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                        </div>
                        <h3 className={styles.errorTitle}>Failed to load notifications</h3>
                        <p className={styles.errorMessage}>{error}</p>
                        <button
                            className={styles.retryButton}
                            onClick={fetchNotifications}
                        >
                            Try Again
                        </button>
                    </div>
                ) : currentNotifications.length > 0 ? (
                    <>
                        <div className={styles.notificationsList}>
                            {currentNotifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={styles.notificationItem}
                                >
                                    <div className={styles.notificationContent}>
                                        <div className={styles.notificationIcon}>
                                            {getNotificationIcon('general')}
                                        </div>
                                        <div className={styles.notificationDetails}>
                                            <p className={styles.notificationMessage}>
                                                {notification.message}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    className={styles.pageButton}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <span className={styles.pageInfo}>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    className={styles.pageButton}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                            </svg>
                        </div>
                        <h3 className={styles.emptyTitle}>
                            No {activeTab === 'old' ? 'old' : 'new'} notifications
                        </h3>
                        <p className={styles.emptyMessage}>
                            {activeTab === 'old'
                                ? 'You don\'t have any old notifications.'
                                : 'You\'re all caught up! No new notifications.'
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationList; 